import React, { createContext, useContext, useMemo } from 'react';
import { useAdminDashboardStats } from '@/hooks/useAdminDashboardStats';

interface AdminStatsContextType {
  totalMembers: number;
  eventsThisMonth: number;
  monthlyRevenueEUR: number;
  engagementRate: number;
  loading: boolean;
}

const AdminStatsContext = createContext<AdminStatsContextType | undefined>(undefined);

export const AdminStatsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const stats = useAdminDashboardStats();
  
  // Mémoriser la valeur pour éviter les re-renders inutiles
  const value = useMemo(() => stats, [
    stats.totalMembers,
    stats.eventsThisMonth,
    stats.monthlyRevenueEUR,
    stats.engagementRate,
    stats.loading
  ]);

  return (
    <AdminStatsContext.Provider value={value}>
      {children}
    </AdminStatsContext.Provider>
  );
};

export const useAdminStats = () => {
  const context = useContext(AdminStatsContext);
  if (context === undefined) {
    throw new Error('useAdminStats must be used within an AdminStatsProvider');
  }
  return context;
};

export const useOptionalAdminStats = (): AdminStatsContextType => {
  const context = useContext(AdminStatsContext);
  if (context === undefined) {
    // Return default values when provider is not available
    return {
      totalMembers: 0,
      eventsThisMonth: 0,
      monthlyRevenueEUR: 0,
      engagementRate: 0,
      loading: false,
    };
  }
  return context;
};